import {
  BodyLinkV3,
  LayoutGrid,
  TypographyRhythm,
  TypographyV3,
} from '@jouzen/ecom-components';
import { useTranslations } from 'next-intl';

import PageLayoutV3 from '@/app/components/PageLayoutV3';
import useFeatureFlag from '@/app/hooks/useFeatureFlag';

import { AccessAndUseSection } from '../_components/AccessAndUseSection';
import { TermsOfDeliverySection } from '../_components/TermsOfDeliverySection';

const Page = () => {
  const enable2024Terms = useFeatureFlag('enable-2024-terms-of-use');
  const t = useTranslations();

  return (
    <PageLayoutV3
      pageName="Terms and Conditions"
      containerProps={{
        name: 'terms-and-conditions-de',
        padding: 'both',
        lang: 'de',
      }}
      footerProps={{
        wrapperClassName: 'bg-white',
      }}
    >
      <LayoutGrid data-cy="terms-and-conditions-de-page">
        <TypographyRhythm className="col-main">
          <TypographyV3 Element="h1" variant="h1" className="break-words">
            ŌURA-NUTZUNGSBEDINGUNGEN
          </TypographyV3>

          <BodyLinkV3 color="inherit" href="/gen2-terms-and-conditions/de">
            Tidligere NUTZUNGSBEDINGUNGEN
          </BodyLinkV3>
          <TypographyV3>
            <em>Letzte Aktualisierung: 2. April 2024</em>
          </TypographyV3>
          <TypographyV3>
            Willkommen und vielen Dank für Ihr Interesse an Ōura! Diese
            Nutzungsbedingungen (die „Vereinbarung“) beschreiben die Bedingungen
            und Konditionen, die für Ihre Nutzung der Website ouraring.com (die
            „Website“), des Smart-Rings, des Ladegeräts und/oder anderer
            elektronischer Geräte (die „Produkte“), einschließlich der mobilen
            Anwendung Ōura (die „mobile App“) (zusammen die „Dienste“) gelten.
            Die Website und die mobile App sind Eigentum von{' '}
            <strong>Ōura Health Oy</strong> und seinen verbundenen Unternehmen
            und Tochtergesellschaften, wie beispielsweise Ouraring Inc.,
            (zusammen „Ōura“ oder „Oura“) und werden von diesen betrieben.
          </TypographyV3>
          <TypographyV3>
            In dieser Vereinbarung beziehen sich „Ōura“ und „Oura“ oder „uns“
            und „wir“ auf unser Unternehmen. Mit „Sie“ oder „Kunde“ sind Sie
            gemeint. Oura und Sie werden in dieser Vereinbarung einzeln als
            „Partei“ und gemeinsam als die „Parteien“ bezeichnet.
          </TypographyV3>
          <TypographyV3>
            Durch den Zugriff auf die Dienste, einschließlich der Website, und
            ihre Nutzung erklären Sie sich ausdrücklich damit einverstanden, an
            alle Bedingungen dieser Vereinbarung und der Datenschutzerklärung
            (verfügbar unter
            <BodyLinkV3
              color="inherit"
              href="https://ouraring.com/privacy-policy-oura-health/de"
              target="_blank"
            >
              https://ouraring.com/privacy-policy-oura-health/de
            </BodyLinkV3>
            ) gebunden zu sein, die durch Verweis einbezogen ist. Wenn Sie mit
            diesen Bedingungen nicht einverstanden sind, dürfen Sie die Dienste
            nicht nutzen.
          </TypographyV3>

          <AccessAndUseSection />

          <TypographyV3 Element="h2" variant="h3">
            Kundenverantwortung
          </TypographyV3>
          <TypographyV3>
            Sie erkennen an, dass Sie allein für Ihre Nutzung der Dienste
            verantwortlich und haftbar sind, ob direkt oder indirekt. Dies
            schließt ein, dass Sie wissen, ob ein solcher Zugriff oder eine
            solche Nutzung durch diese Vereinbarung erlaubt ist oder gegen sie
            verstößt. Darüber hinaus sind Sie allein für die Einhaltung aller
            einschlägigen Gesetze in Bezug auf Ihre Nutzung der Dienste
            verantwortlich. Sie sind ferner verpflichtet, die Dienste
            ausschließlich für rechtmäßige Zwecke zu nutzen und alle
            Aktivitäten, für die Sie die Dienste nutzen, in Übereinstimmung mit
            allen einschlägigen Gesetzen und Vorschriften durchzuführen,
            einschließlich, aber nicht beschränkt auf alle einschlägigen Gesetze
            des Bundes und der Länder über das Angebot und den Verkauf von
            Wertpapieren, über Geldwäsche und Terrorismusbekämpfung.
          </TypographyV3>
          <TypographyV3>
            Sie allein sind verantwortlich für die Sicherstellung und
            Aufrechterhaltung des Zugriffs auf die Dienste und für die
            Möglichkeit ihrer Nutzung, unter anderem durch den Schutz Ihrer
            eigenen kompatiblen Hardware, der mobilen App, des Internetzugangs,
            der Sicherheits-App für das Mobilgerät, der Geräte oder Dienste zur
            Datensicherung und aller anderen Anforderungen. Oura ist nicht für
            die Bereitstellung einer zusätzlichen mobilen App oder von Hardware
            verantwortlich. Sie erkennen ferner an, dass Oura keine
            Verantwortung für Datenverluste oder andere Schäden oder Verluste
            trägt, die in Verbindung mit Ihrer Nutzung der Dienste entstehen,
            einschließlich des Versäumnisses, angemessene Sicherheits- oder
            Sicherungsgeräte oder -dienste bereitzustellen.
          </TypographyV3>
          <TypographyV3>
            Sie sind dafür verantwortlich, dass Oura über genaue und aktuelle
            Informationen für Ihr Kundenkonto verfügt, einschließlich aktueller
            Kontakt- und Zahlungsdaten. Sie sind außerdem dafür verantwortlich,
            das verknüpfte Kunden-E-Mail-Konto regelmäßig auf Mitteilungen von
            Oura zu überprüfen.
          </TypographyV3>
          <TypographyV3>
            Wenn Sie einen Benutzernamen, ein Passwort, eine Datei mit
            Anmeldedaten oder andere Daten („Anmeldedaten“) als Teil eines
            Sicherheitsverfahrens erhalten, müssen Sie diese Informationen
            vertraulich behandeln und dürfen die Anmeldedaten nicht an andere
            Personen oder Organisationen weitergeben. Sie erkennen an, dass Ihr
            Konto und Ihre Anmeldedaten nur für Sie persönlich bestimmt sind,
            und stimmen zu, keiner anderen Person mit Ihrem Benutzernamen, Ihrem
            Passwort oder anderen Sicherheitsinformationen Zugriff auf die
            Dienste oder auf Teile der Dienste zu gewähren. Sie sind
            verpflichtet, Oura unverzüglich über jeden unbefugten Zugriff auf
            Ihre Anmeldedaten oder deren Verwendung sowie über jeden anderen
            Sicherheitsverstoß zu informieren. Oura ist berechtigt, jeden
            Benutzernamen, jedes Passwort, jede Datei mit Anmeldedaten sowie
            jede andere Kennung jederzeit zu deaktivieren, unabhängig davon, ob
            diese von Ihnen gewählt oder von Oura bereitgestellt wurden.
          </TypographyV3>
          <TypographyV3>
            Oura unternimmt unter ökonomischen Gesichtspunkten angemessene
            Anstrengungen, um geeigneten Support für die Dienste
            bereitzustellen. Ungeachtet dessen bildet diese Vereinbarung keine
            Grundlage für einen Anspruch auf ein garantiertes Maß an
            Supportleistungen für die Dienste, eine garantierte Verfügbarkeit
            derselben oder garantierte Antwortzeiten.
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            Zahlung und Gebühren
          </TypographyV3>
          <TypographyV3>
            Bei den kostenpflichtigen Diensten kann es sich um einmalige Käufe
            oder sich automatisch verlängernde Abonnementdienste
            („kostenpflichtige Dienste“) handeln, einschließlich unserer Website
            und mobilen App („Abonnements“). Wir können die kostenpflichtigen
            Dienste jederzeit und aus beliebigem Grund ändern, aussetzen oder
            einstellen, und Oura behält es sich vor, nach eigenem Ermessen zu
            bestimmen, welche Dienste oder Teile davon kostenpflichtig sind.
          </TypographyV3>
          <TypographyV3>
            Kostenpflichtige Dienste können vorbestellte Produkte umfassen, die
            in Zukunft für Sie produziert werden („Vorbestellung“). Ihnen wird
            eine Vorbestellungsgebühr berechnet, wenn Sie eine Vorbestellung
            aufgeben. Das tatsächliche Datum des Versands einer
            entgegengenommenen Vorbestellung hängt von einer Vielzahl von
            Faktoren ab, unter anderem vom Zahlungsdatum Ihrer
            Vorbestellungsgebühr und von Ouras Produktionsplan. Es gibt kein
            garantiertes Versanddatum für Vorbestellungen.
          </TypographyV3>
          <TypographyV3>
            Sie erklären sich damit einverstanden, alle vereinbarten Gebühren
            für kostenpflichtige Dienste zu zahlen, einschließlich aller
            monatlichen Abonnementgebühren, Nutzergebühren, Angebotsgebühren
            sowie sonstigen Gebühren oder Kosten, die Sie als Teil der
            kostenpflichtigen Dienste während des Bestellvorgangs erwerben
            („Gebühren“). Sie erklären sich damit einverstanden, alle Gebühren
            und anfallenden Steuern zu zahlen, die vor Beendigung oder Kündigung
            der Vereinbarung angefallen sind.
          </TypographyV3>
          <TypographyV3>
            Sie ermächtigen Oura, Ihnen die Gebühren für kostenpflichtige
            Dienste in Rechnung zu stellen und das von Ihnen angegebene
            Zahlungsmittel mit dem entsprechenden Betrag zu belasten. Indem Sie
            eine zulässige Zahlungsmethode angeben, sichern Sie Ihr
            Einverständnis und garantieren dafür, dass Sie berechtigt sind, die
            angegebene Zahlungsmethode zu verwenden, und dass Sie uns oder
            unseren externen Zahlungsabwickler ermächtigen, den Gesamtbetrag
            Ihres Einkaufs einschließlich aller anfallenden Steuern und
            sonstigen Gebühren über die angegebene Zahlungsmethode einzuziehen.
            Wenn die Zahlungsmethode nicht überprüft werden kann, ungültig ist
            oder aus anderen Gründen nicht akzeptiert wird, kann Ihr
            kostenpflichtiger Dienst ausgesetzt oder storniert werden. Sie
            müssen alle Probleme lösen, die wir in Bezug auf die von Ihnen
            angegebene Zahlungsmethode feststellen, um mit der Nutzung des
            Dienstes fortfahren zu können. Wenn Sie ein Werbeangebot annehmen
            oder Änderungen an Ihren kostenpflichtigen Diensten vornehmen,
            können die Gebühren, Steuern und Rechnungsbeträge abweichen. Die
            Rechnungsbeträge können auch aufgrund von Änderungen bei anfallenden
            Steuern oder den Wechselkursen variieren. Sie ermächtigen uns oder
            unseren externen Zahlungsabwickler, den entsprechenden Betrag über
            die von Ihnen gewählte Zahlungsmethode einzuziehen. Rückerstattungen
            werden nicht gewährt, es sei denn, dies ist gesetzlich
            vorgeschrieben. Einzelne Rabatte sind nicht mit anderen Rabatten
            kombinierbar. Diese Zahlungsverpflichtung gilt auch, nachdem diese
            Vereinbarung aus jeglichen Gründen beendet oder gekündigt wurde.
          </TypographyV3>
          <TypographyV3>
            Wenn Sie sich für die Finanzierung eines Kaufs über unseren
            Zahlungsabwickler entscheiden und ein oder mehrere Artikel in Ihrer
            Bestellung ein verlängertes Lieferdatum haben, kann es sein, dass
            Ihre Ratenzahlung(en), einschließlich Zinsen, fällig wird/werden,
            bevor wir alle Artikel versenden. Bitte beachten Sie, dass Sie keine
            Zinsen erstattet bekommen, die bereits für einen finanzierten Betrag
            angefallen sind, der später zurückerstattet wird.
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            Abonnements
          </TypographyV3>
          <TypographyV3>
            Bestimmte kostenpflichtige Dienste werden im Rahmen eines
            Abonnements erworben, für das die folgenden Bedingungen gelten:
          </TypographyV3>
          <TypographyV3>
            <strong>
              Ihre Abonnementlaufzeit kann unbegrenzt, monatlich oder jährlich
              sein („Abonnementlaufzeit(en)“); dies wird beim Kauf der
              kostenpflichtigen Dienste angegeben. Ihr Abonnement verlängert
              sich automatisch jeweils um eine weitere Abonnementlaufzeit, bis
              Sie Ihr Abonnement kündigen oder es von Oura ausgesetzt oder
              beendet wird. Sofern von uns nicht anders angegeben, wird Ihnen
              die Abonnementgebühr zuzüglich eventuell anfallender Steuern und
              sonstigen Gebühren vor oder zu Beginn eines jeden
              Abonnementzeitraums über die von Ihnen angegebene Zahlungsmethode
              belastet. Bevor wir die Gebühr für Ihre Abonnementlaufzeit
              einziehen, informieren wir Sie über die geltenden Gebühren, und
              die Abonnementverlängerung erfolgt zu dem Preis, der zu diesem
              Zeitpunkt für den kostenpflichtigen Dienst gilt.
            </strong>
          </TypographyV3>
          <TypographyV3>
            <strong>
              Sie können Ihr Abonnement jederzeit kündigen. Die Kündigung wird
              zum Ende der aktuellen Abonnementlaufzeit wirksam. Um Ihr
              Abonnement und die automatische Zahlung zu kündigen, klicken Sie
              auf der Anzeige Ihrer Kontodaten auf die Schaltfläche „Cancel
              Account“ (Konto kündigen) oder senden Sie eine E-Mail an
              support@ouraring.com. Die Kündigung berechtigt Sie nicht zur
              Rückerstattung zuvor gezahlter Gebühren und Sie erhalten keine
              anteilige Rückerstattung für die verbleibende Abonnementlaufzeit.
              Wenn Sie Ihr Abonnement kündigen, kann es sein, dass Sie weiterhin
              Werbemitteilungen erhalten, es sei denn, Sie widerrufen Ihre
              Einwilligung zum Erhalt dieser Mitteilungen, indem Sie die in den
              Mitteilungen enthaltenen Anweisungen zum Abbestellen befolgen.
            </strong>
          </TypographyV3>
          <TypographyV3>
            <strong>
              Wenn Sie ein Abonnement kündigen, werden nur zukünftige Gebühren
              für Ihr Abonnement storniert. Sie erhalten keine Rückerstattung
              für den aktuellen Abonnementzeitraum, für den Sie bezahlt haben,
              aber Sie haben bis zum Ablauf des Abonnements vollen Zugriff auf
              den Dienst
            </strong>
            . Wir können einigen oder allen unseren Nutzern jederzeit und aus
            einem beliebigen Grund eine Rückerstattung, einen Rabatt oder eine
            andere Gegenleistung („Gutschrift“) gewähren. Die Höhe und Form
            solcher Gutschriften sowie die Entscheidung, sie zu gewähren, liegen
            allein in unserem Ermessen. Die Gewährung einer Gutschrift in einem
            Fall berechtigt Sie nicht zu zukünftigen Gutschriften in ähnlichen
            Fällen und verpflichtet uns auch nicht, in der Zukunft Gutschriften
            zu gewähren.
          </TypographyV3>
          <TypographyV3>
            Wenn Sie Ihren Wohnsitz außerhalb der Vereinigten Staaten haben und
            den Kauf rückgängig machen möchten („Rücktrittszeitraum“), haben Sie
            möglicherweise Anspruch auf eine vollständige Rückerstattung
            innerhalb von vierzehn (14) Tagen, vorausgesetzt, Sie haben sich
            nicht angemeldet oder während des Rücktrittszeitraums als Abonnent
            mit der Nutzung der Dienste begonnen.
          </TypographyV3>
          <TypographyV3>
            Von Zeit zu Zeit bieten wir eventuell die Möglichkeit, bestimmte
            Abonnements für einen bestimmten Zeitraum kostenlos zu testen.
            <strong>
              Vor Beginn des kostenlosen Testzeitraums informieren wir Sie über
              die entsprechenden Abonnementgebühren, die nach Ablauf des
              kostenlosen Testzeitraums anfallen. Wenn Sie Ihr Abonnement nicht
              vor dem Ende des kostenlosen Testzeitraums kündigen, indem Sie die
              oben beschriebenen Schritte unternehmen, werden wir oder unser
              externer Zahlungsabwickler nach Ablauf des kostenlosen
              Testzeitraums die Abonnementgebühr zuzüglich eventuell anfallender
              Steuern und sonstiger Gebühren über die von Ihnen angegebene
              Zahlungsmethode regelmäßig einziehen, solange Ihr Abonnement
              läuft. Sie müssen Ihr Abonnement vor dem Ende des kostenlosen
              Testzeitraums kündigen, um Gebühren zu vermeiden. Wie Sie Ihr
              Abonnement kündigen, ist weiter oben beschrieben.
            </strong>
          </TypographyV3>
          <TypographyV3>
            Ihre Zahlungsinformationen werden von einem externen
            Zahlungsdienstleister verarbeitet und gespeichert. Alle Inhaber von
            kostenpflichtigen Nutzerkonten müssen mindestens eine gültige
            Zahlungsmethode für die Zahlung von Gebühren angeben. Diese werden
            während der Kaufabwicklung näher beschrieben. Sie können zwischen
            monatlichen und jährlichen Abonnementzahlungen wählen. Diese werden
            monatlich oder jährlich in Rechnung gestellt und sind sofort nach
            Erhalt der Rechnung fällig. Sie erkennen an, dass die Gebühren eine
            wiederkehrende Zahlungsfälligkeit haben, und sind für alle
            wiederkehrenden Gebühren vor der Kündigung verantwortlich. Die
            Gebühren werden einen Tag vor dem monatlichen oder jährlichen
            Fälligkeitsdatum mit der von Ihnen angegebenen, gespeicherten
            Zahlungsmethode eingezogen bzw. abgebucht.
          </TypographyV3>
          <TypographyV3>
            Sollten Sie sich nach dem Kauf oder dem Erhalt eines Produkts
            während einer kostenlosen Testphase sechs (6) Monate oder länger
            nicht angemeldet oder die Dienste anderweitig genutzt haben,
            behalten wir uns das Recht vor, Ihr Abonnement zu kündigen, alle
            anstehenden Käufe zu stornieren und Ihnen nur den Kaufpreis des
            Produkts zu erstatten. Sie haben keinen Anspruch auf eine
            Rückerstattung des Wertes des Abonnements während der kostenlosen
            Testphase.
          </TypographyV3>
          <TypographyV3>
            Oura behält sich das Recht vor, die Gebühren für seine
            kostenpflichtigen Dienste oder für bestimmte Funktionen oder Teile
            unserer kostenpflichtigen Dienste jederzeit anzupassen. Sie erkennen
            an, dass Oura die Gebühren für kostenpflichtige Dienste jederzeit
            ändern kann. Im Falle einer solchen Änderung informiert Sie Oura per
            E-Mail an die mit Ihrem Konto verknüpfte E-Mail-Adresse mindestens
            dreißig (30) Tage vor dem Inkrafttreten der Änderung. Ihre
            fortgesetzte Nutzung der Dienste bedeutet, dass Sie die betreffenden
            Gebührenänderungen akzeptieren. Anfallende Steuern gehen allein zu
            Ihren Lasten und werden von Ihnen eingezogen, sofern dies gesetzlich
            vorgeschrieben ist.
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            Daten und Kommunikation
          </TypographyV3>
          <TypographyV3>
            Oura kann Informationen über Ihre Nutzung der Dienste erheben und
            verarbeiten. Sie stimmen zu, dass Oura diese Informationen erhebt
            und verwendet und diese Informationen an Dritte weitergibt, um die
            Dienste bereitzustellen, zu vermarkten und zu verbessern sowie aus
            allen anderen in der Datenschutzerklärung beschriebenen Gründen.
            Alle von Oura erhobenen personenbezogenen Daten werden in
            Übereinstimmung mit den Bestimmungen der Datenschutzerklärung
            behandelt.
          </TypographyV3>
          <TypographyV3>
            <strong>
              Indem Sie den Bedingungen dieser Vereinbarung zustimmen und Oura
              Ihre Kontaktdaten zur Verfügung stellen, geben Sie Ihre
              ausdrückliche Einwilligung, dass Oura, seine verbundenen
              Unternehmen und Vertreter Sie von Zeit zu Zeit unter der von Ihnen
              angegebenen Postadresse, Telefonnummer oder E-Mail-Adresse
              kontaktieren dürfen
            </strong>
            . Ihre Einwilligung bedeutet, dass Sie damit einverstanden sind, von
            Oura und seinen Dienstleistern per Telefon, E-Mail, Textnachricht
            oder auf andere Weise zu jedem Zweck kontaktiert zu werden,
            einschließlich, aber nicht beschränkt auf Benachrichtigungen in
            Bezug auf die Dienste und Ihr Konto, Abonnements, Käufe, verfügbare
            Upgrades, Probleme bei der Abrechnungs- und Zahlungsabwicklung sowie
            zur Telemarketing-Kommunikation. Solche autorisierten Mitteilungen
            können die Verwendung automatischer Wählcomputer oder die Verwendung
            von aufgezeichneten Nachrichten beinhalten. Sie sind für alle
            Gebühren verantwortlich, die Ihnen ggf. von Ihrem(n)
            Dienstanbieter(n) in Rechnung gestellt werden, wenn wir Sie
            kontaktieren. Sie erkennen ferner an, dass Ihre Einwilligung zum
            Vorstehenden keine Bedingung für die Nutzung der Oura-Dienste
            darstellt. Wenn Sie Ihre Einwilligung nicht erteilen möchten, können
            Sie bei uns anfordern, auf die Nicht-Kontakt-Liste gesetzt zu
            werden. Oder nutzen Sie den Abmelde-Link, der in solchen
            Mitteilungen enthalten ist.
          </TypographyV3>
          <TypographyV3>
            Oura lehnt im Rahmen dieser Vereinbarung jegliche Haftung für
            Informationen ab, die Sie Oura zur Verfügung stellen und bei denen
            es sich um elektronische Patientendaten oder ähnliche Informationen
            handelt, die von Ihnen oder einem Endbenutzer bereitgestellt werden,
            sofern gegenteilige Bestimmungen in dieser Vereinbarung oder
            einschlägige Gesetze, Regeln oder Bestimmungen des Bundes oder der
            Länder nichts anderes vorsehen.
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            Rechte an geistigem Eigentum
          </TypographyV3>
          <TypographyV3>
            ŌURA, Ō und OURA sind Marken der Ōura Health Oy. Oura-Inhalte,
            Oura-Produkte, Oura-Funktionen und -Dienste sowie die zugrunde
            liegende Technologie sind durch Urheberrechte, Marken, Patente,
            geistiges Eigentum und andere Gesetze der Vereinigten Staaten und
            anderer Länder geschützt. Alle Rechte sind vorbehalten. Es wird
            Ihnen weder stillschweigend noch anderweitig eine Lizenz oder ein
            Recht zur Verwendung von Marken gewährt, die in den Diensten oder in
            Verbindung mit ihnen verwendet oder angezeigt werden („Marken“). Die
            Dienste können auch Marken, Handelsnamen, Produktnamen und Logos
            Dritter enthalten oder auf diese verweisen, die möglicherweise
            eingetragene Marken der jeweiligen Eigentümer sind. Unter keinen
            Umständen dürfen Sie diese Marken verwenden oder kopieren. In diesem
            Zusammenhang ist nichts so auszulegen, dass eine Lizenz oder ein
            Recht zur Nutzung von Marken, die in Verbindung mit den Diensten
            angezeigt werden, ohne die ausdrückliche schriftliche Einwilligung
            von Oura gewährt wird.
          </TypographyV3>
          <TypographyV3>
            Alle Inhalte, die in Verbindung mit den Diensten und dieser
            Vereinbarung bereitgestellt werden, einschließlich, aber nicht
            beschränkt auf die Website, das Produkt, die mobile App, alle Texte,
            Grafiken, Benutzeroberflächen, visuellen Schnittstellen,
            Fotografien, Bilder/Videos, elektronische Kunst, Klänge/Audio,
            Daten, Kommunikationsprogramme, ausführbaren Code, Computercode und
            Daten (zusammen „Inhalte“), die in einer Vielzahl von Formen
            formatiert, organisiert und zusammengestellt werden, darunter
            Design, Struktur, Auswahl, Koordination, Ausdruck, Aussehen und
            Handhabung, Anordnung, Layouts, Seiten, Bildschirmanzeigen und
            Datenbanken, die in den Inhalten, Diensten und der zugrunde
            liegenden Technologie enthalten sind, sowie alle anderen
            urheberrechtlich geschützten Werke in Verbindung mit den Diensten
            („urheberrechtlich geschützte Werke“) sind ausschließliches Eigentum
            von Oura oder werden von Oura kontrolliert oder lizenziert (oder
            Oura besitzt seinerseits eine Lizenz dafür) und sind durch
            US-amerikanische und internationale Urheberrechtsgesetze geschützt.
            Sie stimmen zu, dass Sie die urheberrechtlich geschützten Werke ohne
            die ausdrückliche schriftliche Einwilligung von Oura weder direkt
            noch indirekt kopieren, reproduzieren, modifizieren, verteilen,
            öffentlich anzeigen oder abgeleitete Werke davon erstellen dürfen.
          </TypographyV3>
          <TypographyV3>
            Wenn Sie Oura per Post, E-Mail, Telefon oder auf andere Weise
            Mitteilungen oder Materialien zur Verfügung stellen, die Änderungen
            an den Diensten vorschlagen oder empfehlen, einschließlich, aber
            nicht beschränkt auf neue Funktionen oder damit verbundene
            Funktionsweisen, Kommentare, Fragen, Vorschläge oder dergleichen
            („Feedback“), steht es Oura frei, dieses Feedback unabhängig von
            anderen Verpflichtungen oder Beschränkungen zwischen den Parteien,
            die dieses Feedback regeln, zu verwenden. Oura steht es frei, Ideen,
            Know-how, Konzepte, Techniken oder andere Rechte an geistigem
            Eigentum, die im Feedback enthalten sind, ohne Anerkennung oder
            Entschädigung einer Partei zu jedem Zweck zu verwenden. Jedoch ist
            Oura nicht verpflichtet, Feedback zu berücksichtigen.
          </TypographyV3>
          <TypographyV3>
            Oura respektiert die Rechte am geistigen Eigentum anderer und es ist
            Teil unserer Unternehmenspolitik, Hinweise auf mutmaßliche Verstöße
            gegen das Urheberrecht oder andere Gesetze zum geistigen Eigentum
            zügig zu bearbeiten und zu prüfen. Alle Hinweise auf mutmaßliche
            Verstöße sollten an den designierten Vertreter von Oura unter
            ip@ouraring.com gesendet werden und müssen die folgenden Angaben
            enthalten: (i) eine Unterschrift (physisch oder elektronisch) des
            Urheberrechtsinhabers oder einer Person, die befugt ist, im Namen
            des Urheberrechtsinhabers zu handeln, (ii) eine Beschreibung des
            geschützten Werks, dessen Urheberrecht Ihrer Ansicht nach verletzt
            wurde, (iii) eine Beschreibung des Materials, das Ihrer Ansicht nach
            gegen das Urheberrecht verstößt und entfernt werden muss oder für
            das der Zugriff gesperrt werden muss, sowie ausreichende
            Informationen, damit die Administratoren von Oura das Material
            lokalisieren können, (iv) ausreichende Angaben, damit wir Sie
            kontaktieren können, z. B. Adresse, Telefonnummer und
            E-Mail-Adresse, (v) eine Erklärung, dass Sie in gutem Glauben davon
            ausgehen, dass die Verwendung des Materials in der beanstandeten
            Weise nicht durch den Urheberrechtsinhaber, seinen Vertreter oder
            das Gesetz genehmigt ist, und (vi) eine Erklärung, dass die
            Informationen in der Mitteilung richtig sind und dass Sie an Eides
            statt versichern, der Urheberrechtsinhaber zu sein oder befugt zu
            sein, im Namen des Inhabers eines Urheberrechts zu handeln, das
            mutmaßlich verletzt wird.
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            Haftungsausschluss
          </TypographyV3>
          <TypographyV3>
            DIE DIENSTE WERDEN IHNEN „WIE BESEHEN“ OHNE MÄNGELGEWÄHR, OHNE
            JEGLICHE GARANTIE UND NACH VERFÜGBARKEIT BEREITGESTELLT. OURA UND
            SEINE TOCHTERGESELLSCHAFTEN, VERBUNDENEN UNTERNEHMEN, LEITENDEN
            ANGESTELLTEN, MITARBEITENDEN, VERTRETER, PARTNER UND LIZENZGEBER
            LEHNEN HIERMIT ALLE GARANTIEN UND BEDINGUNGEN IN BEZUG AUF DIE
            DIENSTE AB, OB AUSDRÜCKLICH ODER STILLSCHWEIGEND, UND SCHLIESSEN
            AUSDRÜCKLICH ALLE STILLSCHWEIGENDEN GARANTIEN IN BEZUG AUF EIGENTUM,
            HANDELSÜBLICHKEIT, EIGNUNG FÜR EINEN BESTIMMTEN ZWECK,
            ZUFRIEDENSTELLENDE QUALITÄT UND NICHTVERLETZUNG VON RECHTEN AUS.
            OURA ÜBERNIMMT KEINE GEWÄHR DAFÜR, DASS DIE NUTZUNG DER DIENSTE
            NICHT BEEINTRÄCHTIGT WIRD, DASS DIE INHALTE STETS VERFÜGBAR SIND,
            DASS IN DEN DIENSTEN ENTHALTENE FUNKTIONEN IHREN ANFORDERUNGEN
            ENTSPRECHEN, DASS DIE DIENSTE FREI VON VIREN ODER ANDEREN
            SCHÄDLICHEN KOMPONENTEN SIND, DASS DER BETRIEB DER DIENSTE
            UNUNTERBROCHEN UND FEHLERFREI IST, DASS FEHLER IN DEN DIENSTEN
            BEHOBEN WERDEN ODER DASS DIE IN DEN DIENSTEN ENTHALTENEN FUNKTIONEN
            MIT ANDEREN MOBILEN APPS ODER ANDERER HARDWARE ODER INNERHALB EINES
            SYSTEMS FUNKTIONIEREN. KEINE MÜNDLICHEN ODER SCHRIFTLICHEN
            INFORMATIONEN ODER RATSCHLÄGE, DIE VON OURA ODER EINEM AUTORISIERTEN
            VERTRETER VON OURA GEGEBEN BZW. ERTEILT WERDEN, STELLEN EINE
            GARANTIE DAR. IN EINIGEN RECHTSORDNUNGEN IST DER AUSSCHLUSS
            STILLSCHWEIGENDER GARANTIEN ODER DIE EINSCHRÄNKUNG GELTENDER
            GESETZLICHER VERBRAUCHERRECHTE NICHT ZULÄSSIG, SO DASS DER OBIGE
            AUSSCHLUSS MÖGLICHERWEISE NICHT GILT.
          </TypographyV3>
          <TypographyV3>
            UNBESCHADET DER VORSTEHENDEN BESTIMMUNGEN GARANTIERT OURA DEM
            ENDVERBRAUCHER, DER DAS PRODUKT URSPRÜNGLICH GEKAUFT HAT, FÜR EIN
            (1) JAHR AB KAUFDATUM, DASS DAS PRODUKT FREI VON MATERIAL- UND
            VERARBEITUNGSFEHLERN IST. Wenn das zum Zeitpunkt des Kaufs bei Ihnen
            vor Ort geltende Recht einen längeren Gewährleistungszeitraum als
            ein (1) Jahr vorschreibt, wird diese Frist in dem Umfang verlängert,
            den das Gesetz vorschreibt. Innerhalb des Gewährleistungszeitraums
            repariert oder ersetzt Oura kostenlos alle Komponenten des Produkts,
            die Mängel aufweisen, die unter die Gewährleistung fallen. Alle
            damit verbundenen Versandkosten gehen zu Ihren Lasten. Die
            Ersatzprodukte können nach unserem Ermessen neu oder überholt sein.
            Die Gewährleistung gilt nicht für (i) normale Abnutzung,
            einschließlich Kratzer und Dellen, (ii) im Produkt enthaltene
            Verschleißteile, wie z. B. Akkus, es sei denn, der Produktschaden
            ist aufgrund eines Material- oder Verarbeitungsfehlers entstanden,
            (iii) Schäden, die darauf zurückzuführen sind, dass Sie das Produkt
            nicht gemäß den dem Ōura-Produkt beiliegenden und auf der Website
            verfügbaren Anweisungen verwendet haben, (iv) Schäden, die durch
            einen Unfall, eine Überschwemmung, einen Brand, eine unsachgemäße
            oder missbräuchliche Verwendung entstanden sind, (v) Schäden durch
            eine Wartung, die von einer nicht von Ōura autorisierten Person
            vorgenommen wurde, oder Schäden, die durch Eingriffe oder
            Veränderungen am Produkt von nicht autorisierten Personen entstanden
            sind, oder (vi) die Verwendung des Produkts mit einer anderen
            Anwendung oder Software als der mobilen App.
          </TypographyV3>
          <TypographyV3>
            Oura behält sich das ausschließliche Recht vor, das Produkt nach
            eigenem Ermessen zu reparieren oder zu ersetzen oder eine
            vollständige Rückerstattung anzubieten. Dieser Rechtsbehelf ist Ihr
            einziger und ausschließlicher Rechtsbehelf bei Sachmängeln. Für im
            Rahmen der Gewährleistung reparierte Produkte und Ersatzprodukte
            gilt eine neue Garantie von neunzig (90) Tagen oder der Restlaufzeit
            der ursprünglichen einjährigen (1) Gewährleistung, je nachdem,
            welcher Zeitraum länger ist.
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            Haftungsbeschränkung
          </TypographyV3>
          <TypographyV3>
            IN KEINEM FALL HAFTEN OURA, SEINE TOCHTERGESELLSCHAFTEN, VERBUNDENEN
            UNTERNEHMEN, LEITENDEN ANGESTELLTEN, MITARBEITENDEN, VERTRETER,
            VERMITTLER, PARTNER UND LIZENZGEBER FÜR SCHÄDEN (GLEICH WELCHER ART
            UND UNABHÄNGIG VON DER RECHTSTHEORIE, AUF DER SIE BERUHEN), DIE SICH
            AUS ODER IN VERBINDUNG MIT IHRER NUTZUNG ODER IHRER UNFÄHIGKEIT ZUR
            NUTZUNG DER DIENSTE ODER DER INHALTE, DIE MIT DEN DIENSTEN VERBUNDEN
            SIND, ODER ANDERER WEBSITES ODER DIENSTE ODER ARTIKEL, DIE ÜBER DIE
            DIENSTE BEZOGEN WERDEN, ERGEBEN. DIES SCHLIESST DIREKTE, INDIREKTE
            UND BESONDERE SCHÄDEN, NEBENSCHÄDEN, FOLGESCHÄDEN UND SCHADENSERSATZ
            MIT STRAFCHARAKTER EIN, WIE UNTER ANDEREM PERSONENSCHÄDEN, SCHMERZEN
            UND LEIDEN, EMOTIONALE BELASTUNG, EINKOMMENSVERLUSTE, ENTGANGENE
            GEWINNE, GESCHÄFTLICHE EINBUSSEN, ENTGANGENE ERWARTETE EINSPARUNGEN,
            ENTGANGENE GESCHÄFTSCHANCEN, GESCHÄFTSUNTERBRECHUNG,
            NUTZUNGSAUSFALL, VERLUST VON FIRMENWERT UND VERLUST VON DATEN EIN.
            DIESE AUSSCHLÜSSE ODER BESCHRÄNKUNGEN GELTEN UNABHÄNGIG DAVON, OB
            DIE SCHÄDEN DURCH UNERLAUBTE HANDLUNGEN (EINSCHLIESSLICH
            FAHRLÄSSIGKEIT), VERTRAGSBRUCH ODER ANDERWEITIG VERURSACHT WURDEN,
            AUCH WENN SIE VORHERSEHBAR WAREN, UND UNABHÄNGIG DAVON, OB OURA VOR
            DER MÖGLICHKEIT SOLCHER SCHÄDEN GEWARNT WURDE ODER NICHT.
          </TypographyV3>
          <TypographyV3>
            DAS VORSTEHENDE BERÜHRT NICHT DIE HAFTUNG, DIE NACH GELTENDEM RECHT
            NICHT AUSGESCHLOSSEN ODER BESCHRÄNKT WERDEN KANN. IN DEM UMFANG, IN
            DEM DIE HAFTUNG NICHT WIE OBEN DARGELEGT AUSGESCHLOSSEN ODER
            BESCHRÄNKT WERDEN KANN, HAFTET OURA UNTER KEINEN UMSTÄNDEN FÜR
            ANSPRÜCHE, DIE ÜBER EINE HÖHE VON 100 EURO HINAUSGEHEN, GANZ GLEICH,
            OB SIE SICH AUF EINEN VERTRAG, EINE UNERLAUBTE HANDLUNG ODER EINE
            ANDERE HAFTUNGSTHEORIE GRÜNDEN.
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            Vorsichtshinweise
          </TypographyV3>
          <TypographyV3>
            DIE DIENSTE STELLEN KEIN MEDIZINPRODUKT DAR, UND SIE ERKENNEN
            AUSDRÜCKLICH AN, DASS DIE DIENSTE KEINE MEDIZINISCHE BERATUNG DURCH
            OURA BEINHALTEN. DIE DIENSTE SIND NICHT ZUR DIAGNOSE, BEHANDLUNG,
            HEILUNG ODER PRÄVENTION VON KRANKHEITEN ODER MEDIZINISCHEN
            BESCHWERDEN BESTIMMT. DIE DIENSTE HABEN LEDIGLICH DEN ZWECK DER
            INFORMATION UND KÖNNEN DIE LEISTUNGEN VON ÄRZTEN ODER MEDIZINISCHEM
            FACHPERSONAL NICHT ERSETZEN.
          </TypographyV3>
          <TypographyV3>
            DIE DIENSTE, EINSCHLIESSLICH ALLER INFORMATIONEN, TEXTE, FOTOS,
            BILDER, ILLUSTRATIONEN, GRAFIKEN, AUDIO- UND VIDEO-INHALTE SOWIE
            AUDIO-VIDEO-CLIPS UND ANDEREN MATERIALIEN, OB VON UNS ODER DRITTEN
            BEREITGESTELLT, SIND NICHT DAZU BESTIMMT, FOLGENDES ZU ERSETZEN, UND
            DÜRFEN SIE NICHT ERSETZEN: (a) DIE BERATUNG DURCH EINEN ARZT ODER
            ANDERE MEDIZINISCHE FACHKRÄFTE, (b) EINEN BESUCH, EINEN ANRUF BEI
            ODER EIN BERATUNGSGESPRÄCH MIT IHREM ARZT ODER EINER ANDEREN
            MEDIZINISCHEN FACHKRAFT ODER (c) DIE INFORMATIONEN AUF ODER IN EINER
            PRODUKTVERPACKUNG ODER AUF EINEM PRODUKTETIKETT.
          </TypographyV3>
          <TypographyV3>
            SOLLTEN SIE GESUNDHEITLICHE FRAGEN HABEN, RUFEN SIE UMGEHEND IHREN
            ARZT ODER EINE MEDIZINISCHE EINRICHTUNG AN ODER SUCHEN SIE DIESE(N)
            AUF. RUFEN SIE IN NOTFÄLLEN SOFORT IHREN ARZT, DEN ÄRZTLICHEN
            BEREITSCHAFTSDIENST ODER DEN RETTUNGSDIENST AN. SIE SOLLTEN NIEMALS
            EINEN ÄRZTLICHEN RAT MISSACHTEN ODER AUFGRUND VON INFORMATIONEN, DIE
            IN DEN DIENSTEN PRÄSENTIERT WERDEN, ÄRZTLICHEN RAT VERSPÄTET
            EINHOLEN. ZUDEM SOLLTEN SIE DIE DIENSTE ODER INFORMATIONEN, DIE IN
            DEN DIENSTEN BEREITGESTELLT WERDEN, NICHT ZUR DIAGNOSE ODER
            BEHANDLUNG EINES GESUNDHEITSPROBLEMS VERWENDEN. DIE ÜBERTRAGUNG UND
            DER EMPFANG VON DIENSTEN, GANZ ODER TEILWEISE, ODER DIE
            KOMMUNIKATION ÜBER DAS INTERNET, PER E-MAIL ODER AUF ANDERE ART UND
            WEISE STELLT KEINE ARZT-PATIENT-, THERAPEUT-PATIENT- ODER ANDERE
            PROFESSIONELLE MEDIZINISCHE BEZIEHUNG ZWISCHEN IHNEN UND OURA DAR.
          </TypographyV3>
          <TypographyV3>
            Sie sollten immer einen Arzt konsultieren, bevor Sie Änderungen an
            Ihren Schlafgewohnheiten oder Ihren Aktivitäten vornehmen, die auf
            den über die Dienste bereitgestellten Informationen beruhen, oder
            wenn Sie Fragen zu medizinischen Beschwerden haben. Oura ist nicht
            für gesundheitliche Probleme verantwortlich, die sich aus den
            Informationen ergeben, die Sie über die Dienste erhalten. Wenn Sie
            Ihre Schlafgewohnheiten oder Ihre Aktivitäten auf der Grundlage der
            Dienste ändern, erklären Sie sich damit einverstanden, dass Sie dies
            auf eigenes Risiko tun. Es ist wichtig, auf die Reaktionen des
            eigenen Körpers zu achten. Wenn Sie beispielsweise unerwartete,
            wiederkehrende oder lang anhaltende Schmerzen, Müdigkeit oder
            Unwohlsein verspüren, weil Sie Ihre Schlafgewohnheiten oder Ihre
            Aktivitäten verändert haben, sollten Sie einen Arzt konsultieren,
            bevor Sie diese Veränderungen beibehalten. Die in den Diensten
            enthaltenen Informationen können irreführend sein, wenn Ihre
            physiologischen Funktionen und Reaktionen aufgrund von Erkrankungen
            oder seltenen natürlichen Unterschieden erheblich vom
            Bevölkerungsdurchschnitt abweichen.
          </TypographyV3>
          <TypographyV3>
            Achten Sie darauf, dass das Produkt, das sie tragen, nicht an festen
            Strukturen oder schweren Gegenständen hängen bleibt. Wenn Sie beim
            Tragen des Produkts eine Rötung oder Hautreizung an Ihrem Finger
            bemerken, nehmen Sie es ab. Wenn die Symptome länger als 2 bis 3
            Tage andauern, ohne dass Sie das Produkt tragen, wenden Sie sich an
            einen Arzt.
          </TypographyV3>
          <TypographyV3>
            Das Produkt von Oura sollte zu keiner Zeit in den Mund genommen
            werden. Es ist kein Spielzeug und auch nicht für den Gebrauch durch
            Kinder bestimmt. Kinder sollten mit diesem Produkt nicht
            unbeaufsichtigt gelassen werden, da es eine Erstickungsgefahr
            darstellen kann.
          </TypographyV3>
          <TypographyV3>
            Die Dienste können Links zu anderen Websites enthalten, die von
            Dritten betrieben werden. Sie erkennen an und stimmen zu, dass
            solche Links lediglich ein Informationsangebot darstellen, aber
            keine Billigung des, Zugehörigkeit oder Beziehung zum oder
            Sponsoring des Anbieters einer solchen verlinkten Website durch Oura
            bedeutet. Ebenso wenig können Rückschlüsse auf die Qualität,
            Zuverlässigkeit, Merkmale und Funktionen der verlinkten Seite
            gezogen werden. Sie erkennen ferner an und stimmen zu, dass Oura in
            keiner Weise (einschließlich und ohne Einschränkung mit Bezug auf
            Verluste oder Verletzungen, die Sie erleiden könnten) für
            Angelegenheiten im Zusammenhang mit der verlinkten Website
            verantwortlich ist. Dies gilt auch und ohne Einschränkung für den
            Inhalt, der auf oder durch eine solche verlinkte Seite
            bereitgestellt wird, und Ihr Vertrauen auf deren Richtigkeit.
            Darüber hinaus sollten Sie sich darüber im Klaren sein, dass Ihre
            Nutzung von Websites Dritter den für diese Website geltenden
            Nutzungsbedingungen unterliegt, einschließlich der
            Datenschutzrichtlinien dieser Website (bzw. der Auswirkungen bei
            deren Fehlen). Wenn ein Dritter auf die Dienste verlinkt, bedeutet
            dies nicht unbedingt eine Billigung seitens, Zugehörigkeit zu,
            Beziehung mit oder Sponsoring durch Oura. Oura ist sich
            möglicherweise nicht einmal bewusst, dass ein Dritter auf die
            Dienste verlinkt hat.
          </TypographyV3>
          <TypographyV3>
            Alle anderen Inhalte, die nicht Eigentum von Oura sind, gehören dem
            jeweiligen Eigentümer. Sie erkennen an und stimmen zu, dass solche
            Inhalte vom jeweiligen Eigentümer bereitgestellt werden und keine
            Billigung des, Zugehörigkeit oder Beziehung zu oder Sponsoring des
            Anbieters solcher Inhalte durch Oura bedeuten. Sie erkennen ferner
            an und stimmen zu, dass Oura in keiner Weise (einschließlich und
            ohne Einschränkung mit Bezug auf Verluste oder Verletzungen, die Sie
            erleiden könnten) für Inhalte verantwortlich oder haftpflichtig ist,
            die von Dritten bereitgestellt werden, ebenso wenig wie für Ihr
            Vertrauen auf deren Richtigkeit. OURA MACHT KEINE ZUSICHERUNGEN UND
            GIBT KEINE GARANTIEN IN BEZUG AUF INHALTE VON DRITTANBIETERN.
          </TypographyV3>
          <TypographyV3>
            Sie erklären sich damit einverstanden, Oura, seine
            Tochtergesellschaften, verbundenen Unternehmen, leitenden
            Angestellten, Mitarbeitenden, Vertreter, Agenten, Partner,
            Lizenzgeber und Rechtsnachfolger in Bezug auf Klagen, Rechtssachen,
            Ansprüche, Schäden, Schulden, Forderungen oder
            Haftungsangelegenheiten zu entschädigen, schadlos zu halten und zu
            verteidigen. Dies umfasst auch angemessene Kosten und
            Anwaltsgebühren, die von einer Person geltend gemacht werden und die
            sich aus oder im Zusammenhang mit (i) Ihrer Nutzung der Dienste,
            einschließlich, aber nicht beschränkt auf Personen, die Ihr Konto
            oder Ihre Anmeldedaten verwenden, (ii) der Verletzung dieser
            Vereinbarung durch Sie oder eine Person, die Ihr Konto oder Ihre
            Anmeldedaten verwendet, (iii) Informationen, die in Verbindung mit
            Ihrem Konto oder Ihren Anmeldedaten verwendet, gespeichert oder
            übertragen werden, (iv) der Verletzung der Rechte Dritter,
            einschließlich, aber nicht beschränkt auf Datenschutz, Werbung,
            geistiges Eigentum oder andere Eigentumsrechte durch Sie oder eine
            Person, die Ihr Konto oder Ihre Anmeldedaten verwendet, oder (v) der
            Verletzung von Gesetzen, Vorschriften oder anderen rechtlichen
            Anforderungen ergeben.
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            Hinweis für Nutzer in Kalifornien
          </TypographyV3>
          <TypographyV3>
            Gemäß Abschnitt 1789.3 des kalifornischen Zivilgesetzbuchs haben
            Nutzer der Dienste in Kalifornien Anspruch auf die folgenden
            spezifischen Verbraucherrechtshinweise:
          </TypographyV3>
          <TypographyV3>
            Die Beschwerdestelle der Abteilung für Verbraucherdienste des
            kalifornischen Ministeriums für Verbraucherangelegenheiten kann
            schriftlich unter der Adresse 1625 N. Market Blvd, Suite N 112,
            Sacramento, Kalifornien 95834, USA, oder telefonisch unter +1 (800)
            952-5210 kontaktiert werden.
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            Kündigung, Beendigung
          </TypographyV3>
          <TypographyV3>
            Diese Vereinbarung bleibt in vollem Umfang in Kraft, bis sie gemäß
            den Bestimmungen in dieser Vereinbarung gekündigt oder beendet wird.
          </TypographyV3>
          <TypographyV3>
            Oura hat das Recht, diese Vereinbarung zu kündigen: (i) aus einem
            beliebigen Grund mit einer Frist von dreißig (30) Tagen ab
            Mitteilung an Sie, (ii) unverzüglich nach einem erheblichen Verstoß
            gegen diese Vereinbarung, mit Ausnahme der Nichtzahlung von
            Gebühren, oder (iii) wegen der Nichtzahlung von Gebühren. Ungeachtet
            der vorstehenden Bestimmungen behält sich Oura das Recht vor, nach
            eigenem Ermessen und ohne vorherige Ankündigung, jederzeit und ohne
            Angabe von Gründen den Zugriff auf alle oder einen Teil der Dienste
            zu entfernen, zu ändern, auszusetzen oder zu deaktivieren.
          </TypographyV3>
          <TypographyV3>
            Sie können die Vereinbarung aus jedem beliebigen Grund kündigen,
            indem Sie Oura unter Wahrung einer Frist von dreißig (30) Tagen
            durch eine E-Mail an support@ouraring.com informieren. Sie sind für
            alle vor und während der Kündigungsfrist anfallenden Gebühren
            verantwortlich.
          </TypographyV3>
          <TypographyV3>
            Die Abschnitte Vorsichtshinweise, Rechte an geistigem Eigentum,
            Daten und Kommunikation, Schadloshaltung, Haftungsausschluss,
            Haftungsbeschränkung, anwendbares Recht, Gerichtsstand,
            verbindliches Schiedsverfahren, Verzicht auf Sammelklagen und
            Zahlungsverpflichtungen für Gebühren, die vor und während der
            Kündigungsfrist angefallen sind, gelten auch nach der Beendigung
            dieser Vereinbarung, unabhängig vom Grund für die Beendigung.
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3" className="break-words">
            Endnutzungsbeschränkungen der US-Regierung
          </TypographyV3>
          <TypographyV3>
            Wenn Sie ein Ministerium oder eine Behörde der US-Bundesregierung
            sind oder im Namen eines solchen Ministeriums oder einer solchen
            Behörde einen Vertrag abschließen, sind Dienste kommerzielle Artikel
            im Sinne von 48 CFR §2.101, die aus kommerzieller Computersoftware
            und der zugehörigen Dokumentation bestehen (siehe
            Begriffsverwendungen in 48 CFR §12.212 oder 48 CFR §227.7202). In
            Übereinstimmung mit 48 CFR §12.212 oder 48 CFR §227.7202-1 bis
            227.7202-4 wird Ihnen eine Lizenz für den Dienst erteilt, die Sie
            nur mit den Rechten gemäß den Bedingungen dieser Vereinbarung
            ausstattet. Wenn Sie ein Auftraggeber der US- Bundesregierung oder
            ein Angestellter eines solchen Auftraggebers sind, der über einen
            GSA- Schedule-Vertrag in seiner Eigenschaft als Vertreter eines
            solchen Auftraggebers einkauft, können die Bedingungen dieser
            Vereinbarung und der Datenschutzerklärung abweichen.
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3" className="break-words">
            Einhaltung von Ausfuhrbestimmungen und Nutzungsbeschränkungen
          </TypographyV3>
          <TypographyV3>
            Sie dürfen die Dienste oder damit zusammenhängende technische
            Informationen weder direkt noch indirekt an einen Bestimmungsort
            oder an eine Person exportieren oder reexportieren, der/die nach
            geltendem Recht verboten ist oder Beschränkungen unterliegt,
            einschließlich aller geltenden US-Exportkontrollgesetze und
            -vorschriften.
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            Anwendbares Recht; Gerichtsstand; verbindliches Schiedsverfahren,
            Verzicht auf Sammelklagen
          </TypographyV3>
          <TypographyV3>
            Alle Handlungen im Zusammenhang mit dieser Vereinbarung, den
            Diensten und Ihrer Beziehung zu Oura unterliegen den Gesetzen des
            Bundesstaates Kalifornien, ungeachtet seiner Grundsätze des
            Kollisionsrechts, UND WERDEN AUSDRÜCKLICH NICHT DURCH DAS
            UN-KAUFRECHT GEREGELT, WO DIESES ANWENDBAR IST. Sie erklären sich
            damit einverstanden, alle Streitigkeiten oder Ansprüche, die sich
            aus dieser Vereinbarung oder den Diensten ergeben oder damit in
            Zusammenhang stehen, durch ein endgültiges und verbindliches
            Schiedsverfahren durch einen einzelnen Schiedsrichter beizulegen.
            Dies gilt auch für Streitigkeiten, die sich aus der Auslegung oder
            Anwendung dieses Abschnitts zur Verbindlichkeit des
            Schiedsverfahrens ergeben oder sich darauf beziehen, einschließlich
            der Durchsetzbarkeit, Widerrufbarkeit oder Gültigkeit. Ungeachtet
            des Vorstehenden kann jede Partei einzig eine Klage auf Unterlassung
            erheben, um die unbefugte Nutzung oder den Missbrauch der Dienste
            oder die Verletzung von geistigem Eigentum zu unterbinden.
            Vorbehaltlich des obligatorischen Schiedsverfahrens erklären sich
            die Parteien unwiderruflich damit einverstanden, alle Klagen zur
            Beilegung oder Durchsetzung von Ansprüchen, die sich aus oder im
            Zusammenhang mit dieser Vereinbarung ergeben, bei den Bundes- oder
            Staatsgerichten von San Francisco, Kalifornien, einzureichen, und
            jede Partei unterwirft sich unwiderruflich der ausschließlichen
            Zuständigkeit dieser Gerichte in allen derartigen Klagen oder
            Verfahren. Sofern dies nicht durch geltendes Recht untersagt ist,
            vereinbaren die Parteien, dass alle Ansprüche oder Klagegründe, die
            sich aus der Nutzung der Dienste oder dieser Vereinbarung ergeben
            oder damit in Zusammenhang stehen, innerhalb eines (1) Jahres nach
            Entstehen des Anspruchs oder des Klagegrundes eingereicht werden
            müssen oder für immer verjähren. Dieser Absatz gilt nicht für
            Nutzer, die in der Europäischen Union ansässig sind. Wenn Sie ein
            Nutzer/eine Nutzerin mit Wohnsitz in der Europäischen Union sind,
            gilt für diesen Vertrag finnisches Recht, und ausschließlich die
            finnischen Gerichte sind für Streitigkeiten im Zusammenhang mit
            diesem Vertrag zuständig. Diese Bestimmung gilt nicht für
            Verbraucher in Ländern, in denen Verträge den örtlichen Gesetzen des
            Landes unterworfen sind, in dem der Verbraucher seinen Wohnsitz hat.
            Für alle Dokumente, Mitteilungen und Auslegungen dieser Vereinbarung
            ist die englische Sprache maßgebend. Sie stimmen außerdem zu, auf
            das Recht zu verzichten, als Vertreter oder Mitglied in einer
            Sammel- oder Vertretungsklage Ansprüche gegen Oura geltend zu
            machen, es sei denn, ein solcher Verzicht ist gesetzlich verboten
            oder wird von einem Gericht als Verstoß gegen die öffentliche
            Ordnung angesehen.
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            Sonstiges
          </TypographyV3>
          <TypographyV3>
            Sie erkennen an, dass Oura das Recht hat, die Nutzung der Dienste zu
            überwachen, um die Einhaltung der Vereinbarung sicherzustellen.
          </TypographyV3>
          <TypographyV3>
            Kein Verzicht auf eine Klausel, eine Bestimmung oder eine Bedingung
            dieser Vereinbarung, sei es durch Verhalten oder anderweitig, in
            einem oder mehreren Fällen, gilt als Verzicht auf eine andere
            Klausel, Bestimmung oder Bedingung dieser Vereinbarung (oder ist als
            solcher auszulegen), unabhängig davon, ob diese ähnlich ist oder
            nicht, noch stellt ein solcher Verzicht einen dauerhaften Verzicht
            auf eine solche Klausel, Bestimmung oder Bedingung dieser
            Vereinbarung dar. Eine Verzichtserklärung ist nur dann verbindlich,
            wenn sie von der betreffenden Partei schriftlich abgegeben wird.
          </TypographyV3>
          <TypographyV3>
            Sie dürfen diese Vereinbarung nicht an eine andere Partei abtreten
            und jeder Versuch, dies zu tun, ist nichtig.
          </TypographyV3>
          <TypographyV3>
            Sollte eine Bestimmung dieser Vereinbarung für rechtswidrig oder
            nicht durchsetzbar befunden werden, so wird diese Bestimmung so weit
            wie möglich durchgesetzt, und die anderen Bestimmungen bleiben
            hiervon unberührt.
          </TypographyV3>
          <TypographyV3>
            Diese Vereinbarung und die Datenschutzerklärung stellen die
            vollständige und ausschließliche Vereinbarung zwischen Ihnen und
            Oura in Bezug auf die Dienste dar und ersetzen alle früheren oder
            gleichzeitigen Mitteilungen, Zusicherungen, Erklärungen und
            Absprachen, ob mündlich oder schriftlich, zwischen den Parteien.
          </TypographyV3>
          <TypographyV3>
            Im Falle eines Widerspruchs zwischen den Bestimmungen dieser
            Vereinbarung und den Bestimmungen der Datenschutzerklärung haben die
            Bestimmungen dieser Vereinbarung Vorrang.
          </TypographyV3>
          <TypographyV3 Element="h2" variant="h3">
            Änderung der Bedingungen und Dienste
          </TypographyV3>
          <TypographyV3>
            Oura behält sich das Recht vor, diese Vereinbarung und/oder die
            Datenschutzerklärung jederzeit und aus beliebigen Gründen nach
            eigenem Ermessen zu aktualisieren, indem aktualisierte Bedingungen
            veröffentlicht werden. Sofern von Oura nicht anders angegeben,
            treten alle Änderungen ab dem Datum der Veröffentlichung in Kraft.
            Oura wird Sie über alle wesentlichen Änderungen der Vereinbarung
            oder der Dienste informieren. Indem Sie weiterhin auf die Dienste
            zugreifen oder diese nutzen, nachdem wir Sie über eine Änderung
            informiert haben, stimmen Sie zu, an die geänderte Vereinbarung
            gebunden zu sein. Wenn die geänderte Vereinbarung für Sie
            inakzeptabel ist, können Sie die Nutzung der Dienste lediglich
            beenden. Oura und seine Drittanbieter können nach eigenem Ermessen
            jederzeit und aus beliebigen Gründen Verbesserungen und/oder
            Änderungen an den beschriebenen Diensten, Funktionen und Preisen
            vornehmen. Die mobile App kann Upgrades, Updates und zusätzliche
            Funktionen herunterladen und installieren, um die Dienste zu
            verbessern, zu erweitern und weiterzuentwickeln. Oura behält sich
            das Recht vor, die Dienste oder Teile davon jederzeit mit oder ohne
            Ankündigung vorübergehend oder dauerhaft zu ändern oder
            einzustellen. Sie stimmen zu, dass Oura weder Ihnen noch Dritten
            gegenüber für eine Änderung, Aussetzung oder Einstellung der Dienste
            haftet.
          </TypographyV3>

          {enable2024Terms ? (
            <TermsOfDeliverySection />
          ) : (
            <>
              <TypographyV3 Element="h1" variant="h1" className="mt-20">
                ŌURA-LIEFERBEDINGUNGEN
              </TypographyV3>
              <TypographyV3>
                <em>Letzte Aktualisierung: 18. Oktober 2021</em>
              </TypographyV3>
              <TypographyV3 Element="h2" variant="h3">
                Europäische Union und USA
              </TypographyV3>
              <TypographyV3>DDP – verzollt geliefert</TypographyV3>
              <TypographyV3>
                Oura ist verantwortlich für die Lieferung der Waren an den
                benannten Ort im Land des Käufers und übernimmt alle Kosten für
                die Lieferung der Waren an den Bestimmungsort, einschließlich
                der Einfuhrzölle und Steuern.
              </TypographyV3>
              <TypographyV3 Element="h2" variant="h3">
                Außerhalb der Europäischen Union und der USA
              </TypographyV3>
              <TypographyV3>
                DAP – Lieferung bis zum Bestimmungsort
              </TypographyV3>
              <TypographyV3>
                Oura übernimmt die Kosten für den Transport bis zum benannten
                Bestimmungsort, mit Ausnahme der Kosten für die
                Einfuhrabfertigung, und übernimmt alle Risiken bis zu dem
                Zeitpunkt, an dem die Ware zur Entladung durch den Käufer bereit
                ist.
              </TypographyV3>
            </>
          )}
          <TypographyV3>Vorherige allgemeine Geschäftsbedingungen</TypographyV3>
          <TypographyV3 className="mt-40">
            {t('legal_disclaimer_translations')}
          </TypographyV3>
        </TypographyRhythm>
      </LayoutGrid>
    </PageLayoutV3>
  );
};

Page.pageName = 'NUTZUNGSBEDINGUNGEN';
Page.isSormusCompatible = true;

export default Page;
